/**
 * Camera Utilities
 * Detect and handle camera capabilities on different devices
 */

/**
 * Check if the device has camera capability
 * @returns {boolean} True if device supports camera access
 */
export const hasCameraSupport = () => {
  // Check if getUserMedia is supported
  return !!(
    navigator.mediaDevices &&
    navigator.mediaDevices.getUserMedia
  );
};

/**
 * Check if device is mobile (has touch and likely has camera)
 * @returns {boolean} True if device appears to be mobile
 */
export const isMobileDevice = () => {
  // Check for touch support
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  // Check user agent for mobile indicators
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  const isMobileUA = mobileRegex.test(navigator.userAgent);
  
  // Check screen size (tablets and phones typically <= 1024px)
  const isSmallScreen = window.innerWidth <= 1024;
  
  return (hasTouch && isMobileUA) || (hasTouch && isSmallScreen);
};

/**
 * Check if device should show camera button
 * (has camera support AND is mobile device)
 * @returns {boolean} True if camera button should be shown
 */
export const shouldShowCameraButton = () => {
  return hasCameraSupport() && isMobileDevice();
};

/**
 * Request camera permission and check if it's available
 * @returns {Promise<boolean>} True if camera access is granted
 */
export const checkCameraPermission = async () => {
  try {
    // Check if permissions API is available
    if (navigator.permissions && navigator.permissions.query) {
      const result = await navigator.permissions.query({ name: 'camera' });
      return result.state === 'granted' || result.state === 'prompt';
    }
    
    // If permissions API not available, assume it's available
    return hasCameraSupport();
  } catch (error) {
    console.warn('Camera permission check failed:', error);
    return hasCameraSupport();
  }
};

/**
 * Capture photo from device camera
 * @param {Object} options - Capture options
 * @param {number} options.maxWidth - Maximum width for captured image
 * @param {number} options.maxHeight - Maximum height for captured image
 * @param {number} options.quality - Image quality (0-1)
 * @returns {Promise<File>} Captured image as File object
 */
export const captureFromCamera = (options = {}) => {
  return new Promise((resolve, reject) => {
    // Create a hidden file input with camera capture
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment'; // Use rear camera by default
    input.style.display = 'none';
    
    // Handle file selection
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) {
        reject(new Error('No image captured'));
        return;
      }
      
      try {
        // If options are provided, we might want to process the image
        // For now, just resolve with the file
        resolve(file);
      } catch (error) {
        reject(error);
      } finally {
        // Cleanup
        document.body.removeChild(input);
      }
    };
    
    // Handle cancellation
    input.oncancel = () => {
      document.body.removeChild(input);
      reject(new Error('Camera capture cancelled'));
    };
    
    // Add to DOM and trigger
    document.body.appendChild(input);
    input.click();
  });
};

/**
 * Convert captured image file to data URL for preview
 * @param {File} file - Image file
 * @returns {Promise<string>} Data URL of the image
 */
export const fileToDataURL = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Get recommended camera settings based on device
 * @returns {Object} Camera settings
 */
export const getRecommendedCameraSettings = () => {
  const isTablet = /iPad|Android/i.test(navigator.userAgent) && window.innerWidth >= 768;
  
  return {
    // Use rear camera for better quality
    facingMode: 'environment',
    // Resolution settings
    width: isTablet ? { ideal: 1920 } : { ideal: 1280 },
    height: isTablet ? { ideal: 1080 } : { ideal: 720 },
  };
};

/**
 * ============================================
 * MediaStream API Functions (Modern Camera)
 * ============================================
 */

/**
 * Check if MediaStream API is supported and should be used
 * @returns {boolean} True if MediaStream camera should be used
 */
export const shouldUseMediaStreamCamera = () => {
  // Check if getUserMedia is available
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    return false;
  }

  // Check if device is mobile
  if (!isMobileDevice()) {
    return false;
  }

  // Check for problematic browsers (e.g., very old Safari)
  const ua = navigator.userAgent;
  const match = ua.match(/(?:iPhone|iPad|iPod).*?OS (\d+)/);
  if (match) {
    const iosVersion = parseInt(match[1], 10);
    // iOS 11+ supports getUserMedia properly
    if (iosVersion < 11) {
      return false;
    }
  }

  return true;
};

/**
 * Initialize MediaStream camera with specified facing mode
 * @param {string} facingMode - 'environment' (rear) or 'user' (front)
 * @returns {Promise<MediaStream>} Camera video stream
 */
export const openMediaStreamCamera = async (facingMode = 'environment') => {
  try {
    // Get recommended settings based on device
    const settings = getRecommendedCameraSettings();
    
    // Request camera with constraints
    const constraints = {
      video: {
        facingMode: { ideal: facingMode },
        width: settings.width,
        height: settings.height,
      },
      audio: false,
    };

    console.log('[cameraUtils] Requesting camera with constraints:', constraints);
    
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    
    console.log('[cameraUtils] Camera stream obtained:', {
      tracks: stream.getVideoTracks().length,
      settings: stream.getVideoTracks()[0]?.getSettings(),
    });
    
    return stream;
  } catch (error) {
    console.error('[cameraUtils] Failed to open camera:', error);
    throw error;
  }
};

/**
 * Capture a photo from the video stream
 * @param {HTMLVideoElement} videoElement - Video element with active stream
 * @returns {Promise<File>} Captured photo as File object
 */
export const capturePhotoFromStream = async (videoElement) => {
  if (!videoElement || !videoElement.videoWidth || !videoElement.videoHeight) {
    throw new Error('Video element is not ready');
  }

  try {
    // Create canvas matching video dimensions
    const canvas = document.createElement('canvas');
    canvas.width = videoElement.videoWidth;
    canvas.height = videoElement.videoHeight;
    
    console.log('[cameraUtils] Capturing photo at resolution:', {
      width: canvas.width,
      height: canvas.height,
    });

    // Draw current video frame to canvas
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }
    
    ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

    // Convert canvas to Blob
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to create image blob'));
            return;
          }

          // Convert Blob to File
          const file = new File(
            [blob],
            `camera-${Date.now()}.jpg`,
            {
              type: 'image/jpeg',
              lastModified: Date.now(),
            }
          );

          console.log('[cameraUtils] Photo captured successfully:', {
            filename: file.name,
            size: file.size,
            type: file.type,
          });

          resolve(file);
        },
        'image/jpeg',
        0.95 // 95% quality - high quality for capture, compression happens later
      );
    });
  } catch (error) {
    console.error('[cameraUtils] Failed to capture photo:', error);
    throw error;
  }
};

/**
 * Stop the camera stream and release resources
 * @param {MediaStream} stream - Camera stream to stop
 */
export const stopCameraStream = (stream) => {
  if (!stream) {
    return;
  }

  try {
    console.log('[cameraUtils] Stopping camera stream');
    
    // Stop all tracks
    stream.getTracks().forEach((track) => {
      track.stop();
      console.log('[cameraUtils] Stopped track:', track.kind, track.label);
    });
  } catch (error) {
    console.error('[cameraUtils] Error stopping camera stream:', error);
  }
};

/**
 * Switch between front and rear cameras
 * @param {MediaStream} currentStream - Current camera stream to stop
 * @param {string} currentFacingMode - Current facing mode
 * @returns {Promise<{stream: MediaStream, facingMode: string}>} New stream and facing mode
 */
export const switchCamera = async (currentStream, currentFacingMode) => {
  try {
    console.log('[cameraUtils] Switching camera from:', currentFacingMode);
    
    // Stop current stream
    stopCameraStream(currentStream);

    // Toggle facing mode
    const newFacingMode = currentFacingMode === 'environment' ? 'user' : 'environment';

    // Open new stream with opposite facing mode
    const newStream = await openMediaStreamCamera(newFacingMode);

    return {
      stream: newStream,
      facingMode: newFacingMode,
    };
  } catch (error) {
    console.error('[cameraUtils] Failed to switch camera:', error);
    throw error;
  }
};

/**
 * Get list of available video input devices (cameras)
 * @returns {Promise<Array>} List of available cameras
 */
export const getAvailableCameras = async () => {
  try {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      console.warn('[cameraUtils] enumerateDevices not supported');
      return [];
    }

    const devices = await navigator.mediaDevices.enumerateDevices();
    const cameras = devices.filter((device) => device.kind === 'videoinput');

    console.log('[cameraUtils] Available cameras:', cameras.length);
    
    return cameras;
  } catch (error) {
    console.error('[cameraUtils] Failed to enumerate devices:', error);
    return [];
  }
};

